import { Editor } from '@tldraw/tldraw'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { createOriginalAssetFromFile } from 'components/Design/UploadsTab/insertImage'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { ImagePickerModal } from 'components/ImagePickerModal/ImagePickerModal'
import { ImagePickerModalContainerProps, Provider } from 'components/ImagePickerModal/Types'
import { ImageShapeUtil } from 'components/VisualStudioPlayground/Shapes/ImageShape/ImageShapeUtil'
import { ImageShape } from 'components/VisualStudioPlayground/Shapes/ImageShape/Types'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { SelectedImage } from 'types'
import { useModals } from 'almanac-ui/Modal/ModalStack'
import { MaskShapeUtil } from 'components/VisualStudioPlayground/Shapes/MaskShape/MaskShapeUtil'
import { MaskShape } from 'components/VisualStudioPlayground/Shapes/MaskShape/Types'
import { useRootStore } from 'stores/RootStore'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ImagePickerDrawer } from 'components/ImagePickerModal/ImagePickerDrawer/ImagePickerDrawer'
import { reportError } from 'utils/error'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'

interface Props {
  editor: Editor
  selectedShape: ImageShape | MaskShape
}

export const useReplaceImage = ({ editor, selectedShape }: Props) => {
  const { openModal, closeModal } = useModals()
  const { drawersStore } = useRootStore()
  const { uploadImageFiles } = useImportMediaFiles()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectQuery = useUploadsProject(workspaceId)
  const { isMobile } = useResponsiveDesign()
  const { mutateAsync: createMediaObjects } = useCreateMediaObjects()
  const isNewImageModalEnabled = useFlipperFlag('new_image_menu')

  let accept = 'image/jpeg,image/png,image/webp'

  if (selectedShape.type === 'image') {
    accept += ',image/svg+xml'
  }

  const replace = () => {
    const props: ImagePickerModalContainerProps = {
      hiddenProviders: new Set<Provider>(['gifs']),
      defaultProvider: 'uploads',
      accept,
      maxCount: 1,
      uploadImages: async (files: File[], context) => {
        if (context.provider !== 'uploads') {
          const objectsParams = files.map((file, index) => {
            const image = context.selectedImages.find((_image, i) => i === index)
            return {
              file,
              filename: file.name,
              height: image?.height,
              width: image?.width,
            }
          })
          const objects = await createMediaObjects(objectsParams)
          return objects.map(
            (object) =>
              ({
                id: '',
                url: object.source_url,
                mime_type: object.mime_type,
                height: object.height,
                width: object.width,
              } as SelectedImage),
          )
        }
        const uploadedImages = await uploadImageFiles(files, uploadsProjectQuery.data?.project?.id)
        if (!uploadedImages) {
          return []
        }
        return uploadedImages.map(
          (image) =>
            ({
              id: image.id,
              url: image.source_url,
            } as SelectedImage),
        )
      },
      title: 'Select Image',
      onConfirm: async (images, context) => {
        const image = images[0]
        if (!selectedShape) {
          return
        }

        let asset
        if (context.provider === 'uploads') {
          if (!image.id) {
            reportError('Cannot replace image with upload without ID')
            return
          }
          const response = await MediaFileClient.getAlmanacFile(image.id)
          if (response.type !== SUCCESS_RESPONSE_TYPE) {
            return
          }
          asset = await createOriginalAssetFromFile(response.data.almanac_file as IMediaFile)
        } else {
          asset = await createOriginalAssetFromFile({
            id: '',
            source_url: image.url,
            mime_type: image.mime_type,
            height: image.height,
            width: image.width,
          } as IMediaFile)
        }

        editor.createAssets([asset])

        switch (selectedShape.type) {
          case 'image':
            const imageUtil = editor.getShapeUtil(selectedShape) as ImageShapeUtil
            imageUtil.replace(selectedShape as ImageShape, asset.id)
            break
          case 'mask':
            const maskUtil = editor.getShapeUtil(selectedShape) as MaskShapeUtil
            maskUtil.replace(selectedShape as MaskShape, asset.id)
        }

        closeModal()
        drawersStore.closeDrawer()
      },
    }

    if (isMobile) {
      drawersStore.openDrawer(ImagePickerDrawer, {
        ...props,
        defaultProvider: undefined,
        confirmLabel: 'Insert Image',
      })
      return
    }

    if (isNewImageModalEnabled) {
      openModal(ImageMenuModal, {
        accept: ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
        hiddenProviders: ['stockVideo', 'gifs'],
        onUse: async (selectedImages: ImageMenuSelectedImage[]) => {
          let images: ImageMenuSelectedImage[] = []

          if (['uploads', 'brandKits', 'device'].includes(selectedImages[0].provider)) {
            images = selectedImages
          } else {
            const dismiss = showToastMessage('Processing...', 'loading', undefined, 0)
            try {
              const files = await Promise.all(
                selectedImages.map(async (image) => {
                  const response = await fetch(image.url)
                  const blob = await response.blob()
                  const type = blob.type || image.mime_type || ''
                  return new File([blob], image.original_filename || 'filename', { type })
                }),
              )
              const objectsParams = files.map((file, index) => {
                const image = selectedImages.find((_image, i) => i === index)
                return {
                  file,
                  filename: file.name,
                  height: image?.height,
                  width: image?.width,
                }
              })
              const objects = await createMediaObjects(objectsParams)
              images = objects.map(
                (object) =>
                  ({
                    id: '',
                    url: object.source_url,
                    mime_type: object.mime_type,
                    height: object.height,
                    width: object.width,
                  } as ImageMenuSelectedImage),
              )
            } catch (error) {
              showToastMessage('Failed to process images. Please try again.', 'error')
              throw error
            } finally {
              dismiss()
            }
          }
          const image = images[0]
          if (!selectedShape) {
            return
          }

          let asset
          if (image.provider === 'uploads') {
            if (!image.id) {
              reportError('Cannot replace image with upload without ID')
              return
            }
            const response = await MediaFileClient.getAlmanacFile(image.id)
            if (response.type !== SUCCESS_RESPONSE_TYPE) {
              return
            }
            asset = await createOriginalAssetFromFile(response.data.almanac_file as IMediaFile)
          } else {
            asset = await createOriginalAssetFromFile({
              id: '',
              source_url: image.url,
              mime_type: image.mime_type,
              height: image.height,
              width: image.width,
            } as IMediaFile)
          }

          editor.createAssets([asset])

          switch (selectedShape.type) {
            case 'image':
              const imageUtil = editor.getShapeUtil(selectedShape) as ImageShapeUtil
              imageUtil.replace(selectedShape as ImageShape, asset.id)
              break
            case 'mask':
              const maskUtil = editor.getShapeUtil(selectedShape) as MaskShapeUtil
              maskUtil.replace(selectedShape as MaskShape, asset.id)
          }

          closeModal()
        },
        maxCount: 1,
      })

      return
    }

    openModal(ImagePickerModal, props)
  }

  return {
    replace,
  }
}
